/**
 * Sell page `fromRequest` prefill (P0).
 * One owner: parse URL params → patch for single / bulk sell forms.
 * Does not invent category taxonomy — only applies what the href carried.
 *
 * Price: buyer max is a budget ceiling, not an ask. Do **not** prefill
 * `formData.price` — sell market guidance owns the asking price.
 * `requestMaxPrice` may still be on the URL for a future soft hint.
 */

import type { ItemCategory } from "@/lib/types";
import type { SellListingFormData } from "@/lib/listing/sellListingFormTypes";
import type { BulkListingItemDraft } from "@/lib/listing/bulkListingTypes";
import {
  formatPreferenceSummary,
  normalizePreferenceAttrs,
  type PreferenceAttrs,
} from "./preferenceAttrs";

/** Query keys written by `buildSellFromRequestHref`. */
export const SELL_FROM_REQUEST_PARAM = {
  flag: "fromRequest",
  /** Stable join key for PostHog funnel (demand strip → sell → submit). */
  requestId: "requestId",
  title: "requestTitle",
  description: "requestDescription",
  category: "requestCategory",
  subcategory: "requestSubcategory",
  location: "requestLocation",
  maxPrice: "requestMaxPrice",
  prefs: "requestPrefs",
} as const;

export type SellFromRequestPrefill = {
  /** Source item request id when present on the URL. */
  itemRequestId: string | null;
  title: string;
  description: string;
  category: ItemCategory | "";
  subcategory: string;
  location: string;
  /**
   * Buyer budget ceiling from the request (₦), when present.
   * Informational only — never written into the sell price field.
   */
  buyerBudgetMax: number | null;
  brand: string;
  size: string;
  model: string;
  preferenceAttrs: PreferenceAttrs;
};

function parseBuyerBudgetMax(maxPriceRaw: string | null): number | null {
  if (!maxPriceRaw?.trim()) return null;
  const n = Number(maxPriceRaw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return n;
}

function scalarToFormString(value: unknown): string {
  if (typeof value === "string") return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "";
}

/**
 * Seed description: buyer notes + preference summary when present.
 */
export function buildSellDescriptionFromRequest(input: {
  notes: string;
  preferenceAttrs: PreferenceAttrs;
}): string {
  const notes = input.notes.trim();
  const prefsLine = formatPreferenceSummary(input.preferenceAttrs);
  const parts: string[] = [];
  if (prefsLine) {
    parts.push(`Buyer asked for: ${prefsLine}`);
  }
  if (notes) {
    parts.push(notes);
  }
  return parts.join("\n\n").slice(0, 2000);
}

/**
 * Parse sell-page search params into a structured prefill.
 * Returns null when `fromRequest` is not set.
 */
export function parseSellFromRequestSearchParams(
  params: URLSearchParams | { get: (key: string) => string | null },
): SellFromRequestPrefill | null {
  if (params.get(SELL_FROM_REQUEST_PARAM.flag) !== "true") {
    return null;
  }

  let preferenceAttrs: PreferenceAttrs = {};
  const prefsRaw = params.get(SELL_FROM_REQUEST_PARAM.prefs);
  if (prefsRaw?.trim()) {
    try {
      preferenceAttrs = normalizePreferenceAttrs(JSON.parse(prefsRaw));
    } catch {
      preferenceAttrs = {};
    }
  }

  const notes = (params.get(SELL_FROM_REQUEST_PARAM.description) ?? "").trim();
  const category = (params.get(SELL_FROM_REQUEST_PARAM.category) ?? "").trim();
  const subcategory = (
    params.get(SELL_FROM_REQUEST_PARAM.subcategory) ?? ""
  ).trim();

  const itemRequestId = (
    params.get(SELL_FROM_REQUEST_PARAM.requestId) ?? ""
  ).trim();

  return {
    itemRequestId: itemRequestId || null,
    title: (params.get(SELL_FROM_REQUEST_PARAM.title) ?? "").trim(),
    description: buildSellDescriptionFromRequest({ notes, preferenceAttrs }),
    category: (category || "") as ItemCategory | "",
    subcategory,
    location: (params.get(SELL_FROM_REQUEST_PARAM.location) ?? "").trim(),
    buyerBudgetMax: parseBuyerBudgetMax(
      params.get(SELL_FROM_REQUEST_PARAM.maxPrice),
    ),
    brand: scalarToFormString(preferenceAttrs.brand),
    size:
      scalarToFormString(preferenceAttrs.size) ||
      scalarToFormString(preferenceAttrs.screen_inches),
    model: scalarToFormString(preferenceAttrs.model),
    preferenceAttrs,
  };
}

/**
 * Apply prefill onto single-listing form state (non-empty fields only).
 * Does not set `price` — market guidance owns the ask.
 */
export function applySellFromRequestToFormData(
  prev: SellListingFormData,
  prefill: SellFromRequestPrefill,
): SellListingFormData {
  return {
    ...prev,
    title: prefill.title || prev.title,
    description: prefill.description || prev.description,
    category: (prefill.category || prev.category) as ItemCategory,
    subcategory: prefill.subcategory || prev.subcategory,
    location: prefill.location || prev.location,
    brand: prefill.brand || prev.brand,
    size: prefill.size || prev.size,
    model: prefill.model || prev.model,
  };
}

/**
 * Apply prefill onto the first bulk draft row.
 * Does not set `price` — market guidance owns the ask.
 */
export function applySellFromRequestToBulkItem(
  prev: BulkListingItemDraft,
  prefill: SellFromRequestPrefill,
): BulkListingItemDraft {
  return {
    ...prev,
    title: prefill.title || prev.title,
    description: prefill.description || prev.description,
    category: (prefill.category || prev.category) as ItemCategory | "",
    subcategory: prefill.subcategory || prev.subcategory,
    brand: prefill.brand || prev.brand,
    size: prefill.size || prev.size,
    model: prefill.model || prev.model,
  };
}
